import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { absoluteTime, agoPhrase } from "../../lib/time";
import type { CompanyDetail } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * "Somebody should be looking after this account."
 *
 * The CRM already knows which accounts have no manager -- there are hundreds,
 * most of them dormant, and a queue that long is one nobody works. This is the
 * human filter over the top: a rep who has just spoken to a customer, or
 * recognised a name going past in the unfiled list, ticks it, and the companies
 * dashboard has a tab of exactly those.
 *
 * The difference between this and the unowned queue is judgement, and that is
 * the whole feature. "Unowned" is a fact about the database; "opportunity" is
 * somebody saying this one is worth somebody's time. One is a report and the
 * other is a recommendation, and only the second is a list a manager can work
 * top to bottom.
 *
 * ## Why it is in the header and not a panel
 *
 * It was a panel in the third column, which is where the account's *details*
 * live, and it sat below the AI summary on a page that scrolls. Jade's call was
 * that this belongs next to "Nobody manages this account" -- the sentence it is
 * an answer to. The header does not scroll, so the flag is reachable from
 * anywhere on a two-year timeline, and the state is legible from the place
 * somebody already reads to find out who owns the account.
 *
 * It costs the panel's explanatory prose. That is the trade: a tick box beside
 * the problem it solves gets used, and a paragraph in a column nobody scrolls
 * to does not. What survives is the tooltip and the reason.
 *
 * ## It disappears on its own
 *
 * Assigning an account manager clears the flag, server-side, in the same write.
 * Nobody has to remember to come back and untick it -- which is exactly the
 * kind of second step that never happens, and which would fill the tab with
 * accounts that were dealt with weeks ago until people stopped opening it.
 *
 * That is also why this renders nothing on a managed account rather than
 * appearing unticked: on an account with a manager the claim has no truth
 * value, and the API refuses it outright. A control the server will reject is
 * worse than no control.
 */
export function OpportunityToggle({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();
  const starred = company.opportunityStarredAt !== null;

  /*
   * The note is only being *written* while the little field is open. Kept
   * separate from `company.opportunityNote` rather than initialised from it on
   * every render, so a refetch landing mid-sentence cannot overwrite what
   * somebody is typing.
   */
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");

  const set = useMutation({
    mutationFn: (input: { starred: boolean; note?: string | null }) =>
      crmApi.setOpportunity(company.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(company.id),
      });
      /* The dashboard's tab and its count both move on this, and so does any
         company list filtered to unowned accounts. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmAmScopes });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompaniesRoot,
      });
    },
  });

  if (company.ownerUserId !== null) return null;

  /**
   * Who flagged it and when, on the tick box itself.
   *
   * The panel said this in a sentence and there is no room for one here. A
   * tooltip is the honest substitute: it is the answer to a question somebody
   * asks occasionally ("who thought this?"), not something the row has to
   * carry, and the reason -- which is asked far more often -- stays visible
   * beside it instead.
   */
  const flaggedBy = starred
    ? [
        "Flagged as needing an account manager",
        company.opportunityStarredBy
          ? ` by ${company.opportunityStarredBy.name}`
          : "",
        company.opportunityStarredAt
          ? `, ${agoPhrase(company.opportunityStarredAt)} (${absoluteTime(company.opportunityStarredAt)})`
          : "",
        ". It appears under “Needs account managing” on the companies dashboard,",
        " and clears itself as soon as somebody is assigned.",
      ].join("")
    : "Flag this as an account somebody should be managing. It appears under “Needs account managing” on the companies dashboard.";

  return (
    <span className="flex items-center gap-1.5">
      <label
        title={flaggedBy}
        className={cn(
          "flex items-center gap-1.5",
          starred ? "text-brand-700 dark:text-brand-300" : "text-muted",
          set.isPending && "opacity-60",
        )}
      >
        <input
          type="checkbox"
          checked={starred}
          disabled={set.isPending}
          onChange={(event) => {
            /*
             * Ticking flags it immediately, with no note, rather than opening
             * the reason field and waiting. The flag is the useful half and a
             * reason is optional -- making somebody finish a sentence before
             * the tick counts is how a one-click action becomes one nobody
             * bothers with. The field opens *after*, already saved.
             */
            const next = event.target.checked;
            /* Either way the note starts empty: ticking has nothing to say
               yet, and unticking should not leave last week's reason behind to
               reappear the next time somebody flags it. */
            set.mutate({ starred: next, note: null });
            setNote("");
            setEditing(next);
          }}
          className="size-3.5 shrink-0 accent-brand-600"
        />
        {starred ? "★" : "☆"} Opportunity
      </label>

      {/*
        The reason, beside the tick rather than behind the tooltip. Without it
        the dashboard tab is a list of names and whoever picks one up starts
        from nothing -- "ordering monthly, nobody has ever rung them" is most of
        the value of the flag, and a note nobody can see is a note nobody
        writes.
      */}
      {starred ? (
        editing ? (
          <NoteField
            initial={company.opportunityNote ?? ""}
            value={note}
            onChange={setNote}
            pending={set.isPending}
            onCommit={(value) =>
              /*
               * Closed on success rather than on click, so a note that fails
               * to save stays on screen with the error beside it. Closing
               * optimistically would swallow what somebody typed.
               */
              set.mutate(
                { starred: true, note: value.trim() || null },
                { onSuccess: () => setEditing(false) },
              )
            }
            onCancel={() => setEditing(false)}
          />
        ) : (
          <button
            type="button"
            title={company.opportunityNote ?? "Say why, for whoever picks it up"}
            onClick={() => {
              setNote(company.opportunityNote ?? "");
              setEditing(true);
            }}
            className={cn(
              "max-w-[18rem] truncate rounded border border-dashed border-line px-1.5 py-0.5 text-[11px]",
              company.opportunityNote
                ? "text-muted hover:text-brand-600"
                : "text-faint hover:text-brand-600",
            )}
          >
            {company.opportunityNote ?? "why?"}
          </button>
        )
      ) : null}

      {set.isError ? (
        <span className="text-[11px] text-sla-breached">That did not work.</span>
      ) : null}
    </span>
  );
}

/**
 * The reason, as one line that saves on Enter.
 *
 * Deliberately not a form. It sits inside the page header, which is not a form
 * and must not become one -- an Enter here submitting something else, or a
 * stray `<form>` nesting inside the header's controls, is the sort of bug that
 * only shows up on the one browser nobody tested.
 *
 * Blur commits rather than discards. Somebody who types a reason and clicks
 * away has said what they meant; throwing it away to be tidy about "unsaved
 * changes" would lose the more valuable half of the flag.
 */
function NoteField({
  initial,
  value,
  onChange,
  pending,
  onCommit,
  onCancel,
}: {
  initial: string;
  value: string;
  onChange: (next: string) => void;
  pending: boolean;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  return (
    <input
      ref={input}
      value={value}
      disabled={pending}
      maxLength={255}
      placeholder="Ordering monthly, nobody has ever rung them"
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(value);
        } else if (event.key === "Escape") {
          /* Stopped as well as prevented: this sits inside a page that closes
             dialogs on Escape, and all that was meant was "not this field". */
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
      onBlur={() => {
        /* Nothing typed and nothing there before: close rather than write an
           empty note over an empty note, which would flash the whole header. */
        if (value.trim() === initial.trim()) onCancel();
        else onCommit(value);
      }}
      className={cn(
        "w-64 rounded border border-line bg-surface px-1.5 py-0.5 text-[11px] text-content",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
      )}
    />
  );
}
